import axios from 'axios';
import invariant from 'invariant';
import validate from 'validate.js';
import localStorageTemplates from 'common/local-storage-templates';
import { DOZI_INBOX_URL, BE_ENDPOINT_ZI, BE_ENDPOINT_NON_ZI } from 'config.js';
import convertErrorMsg from 'utils/convertErrorMsg';
import history from 'utils/history';

const prefix = 'path: utils/request.js';

/**
 * A way to provide descriptive errors
 * in development but generic errors in production.
 */

const checkRequest = ({ method, url, config }) => {
  const constraints = {
    method: validate.contains(['get', 'post', 'put', 'patch', 'delete'], method),
    url: validate({ url }, { url: { url: true } }) === undefined ? true : false,
    config: validate.isObject(config),
  };

  invariant(
    constraints.method,
    `${prefix} - method should be oneOf ['get', 'post', 'put', 'patch', 'delete']`
  );
  invariant(constraints.url, `${prefix} - url is not a valid url`);

  if (config) {
    invariant(constraints.config, `${prefix} - config is not an object`);
  }
};

const getActiveBE = () => {
  let activeBE = '';
  if (window.appDetails.activeBE) {
    activeBE = window.appDetails.activeBE;
  } else if (window.location.host.includes('zoominfo.com')) {
    activeBE = BE_ENDPOINT_ZI;
    window.appDetails.activeBE = activeBE;
  } else {
    activeBE = BE_ENDPOINT_NON_ZI;
    window.appDetails.activeBE = activeBE;
  }
  return activeBE;
};

export const buildFullUrl = endpoint => {
  const baseUrl = getActiveBE();
  const lastChar = baseUrl.slice(-1);
  const validBaseUrl = lastChar === '/' ? baseUrl : `${baseUrl}/`;
  return `${validBaseUrl}${endpoint}`;
};

/**
 * @dev - request handle Promise based HTTP client for the browser and node.js
 *
 * @param {string} method - oneOf ['get', 'post', 'put', 'patch', 'delete']
 * @param {string} endpoint - e.g 'app/register'
 * @param {object} config - an object
 * https://github.com/axios/axios#request-config
 */
export default async function request({ method, endpoint, config, projectName }) {
  let url = '';

  if (projectName && projectName.length) {
    url = `https://${projectName}.api.insent.ai/${endpoint}`;
  } else {
    url = buildFullUrl(endpoint);
  }

  checkRequest({ method, url, config });

  const networkStatus = localStorage.getItem(localStorageTemplates.insentNetworkStatus) || 'online';
  if (networkStatus === 'offline' || !navigator.onLine) {
    return Promise.reject({ response: { data: { errorMsg: 'offline' } } });
  }

  try {
    const response = await axios.request({
      method,
      url,
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
      },
      ...config,
    });

    return response;
  } catch (error) {
    if (error.response && error.response.status === 401) {
      const isNonAuthUrl = window.location.pathname.match(/\/verify\//);
      if (!isNonAuthUrl) {
        const errorMessage = convertErrorMsg(error);
        if (errorMessage && errorMessage?.toLowerCase()?.includes('authentication')) {
          if (window.appDetails?.isZIDashboard) {
            window.location.href = `${DOZI_INBOX_URL}`;
          } else {
            localStorage.clear();
            window.location.reload();
          }
        } else if (
          errorMessage &&
          errorMessage?.toLowerCase()?.includes('your password has expired')
        ) {
          history.push('/passwordChange');
        }
        throw error;
      } else {
        throw error;
      }
    } else {
      throw error;
    }
  }
}
